import React, { useState } from 'react'
import Navbar from '../components/Navbar.js'
import Footer from '../components/Footer.js'
import { Helmet } from 'react-helmet-async'

const ACCENT = '#C96A4A'
const INK = '#1A1A1A'
const PAPER = '#F4F1EA'

// TODO: replace with the direct URL of the published article once it is live
const NLJ_ARTICLE_URL = 'https://www.newlawjournal.co.uk'

const sections = [
  {
    number: '01',
    title: 'The file stands still',
    body: "Over two to three years, witnesses leave, memories fade, and the fee-earner who took the instructions may be at another firm. Costs need re-estimating, clients need re-briefing and evidence needs refreshing. None of that work brings the case closer to resolution."
  },
  {
    number: '02',
    title: 'The 12 weeks now matter',
    body: "Since 1 December 2025, the ACAS conciliation window runs to twelve weeks, not six. Historically only around 13–15% of Early Conciliation notifications have ended in a COT3 settlement. With hearings this far out, the pre-action period is where most disputes now have to be resolved."
  },
  {
    number: '03',
    title: 'Where firms get stuck',
    body: "The obstacle is rarely legal obstinacy. It is an information gap at intake: untangling the claimant's narrative and checking limitation dates before an offer can be priced. That is slow, manual work, and hiring more fee-earners adds capacity without shortening the time any one of them needs to reach a defensible number."
  },
  {
    number: '04',
    title: 'Who sets the terms',
    body: "The side that puts a credible exposure figure on the table first, benchmarked against Vento brackets and mounting defence costs, sets the terms of settlement. Claimants are also using Subject Access Requests to obtain internal correspondence before an ET1 is lodged, so respondents need an evidence strategy before conciliation starts."
  },
  {
    number: '05',
    title: 'How we plan to help',
    body: "We're building LexiTrove as a full matter-cycle tool, so the facts of your matter are already in the system. We use them to create a sandbox that shows how the case may play out at tribunal: limitation dates flagged, SAR disclosure scanned, and an exposure breakdown covering injury to feelings against the Vento bands, Polkey risk and projected defence costs. The result is a partner-ready figure to anchor the offer before conciliation starts."
  }
]

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '0.8125rem',
  fontFamily: "'Jost', sans-serif",
  backgroundColor: PAPER,
  border: '1px solid black',
  boxSizing: 'border-box'
}

const labelStyle = {
  fontSize: '0.75rem',
  color: '#333',
  display: 'block',
  marginBottom: '0.35rem'
}

// Responsive layout: two columns on desktop with a pinned form, stacked on narrow screens
const layoutCss = `
  .pa-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
    min-height: calc(100vh - 70px);
    border-bottom: 1px solid black;
  }
  .pa-left {
    padding: 5rem 6% 5rem 8%;
    border-right: 1px solid black;
  }
  .pa-right {
    background-color: ${PAPER};
    padding: 5rem 6%;
  }
  .pa-sticky {
    position: sticky;
    top: 2rem;
    max-width: 440px;
  }
  @media (max-width: 860px) {
    .pa-grid { grid-template-columns: minmax(0, 1fr); }
    .pa-left { padding: 3rem 6%; border-right: none; border-bottom: 1px solid black; }
    .pa-right { padding: 3rem 6%; }
    .pa-sticky { position: static; max-width: none; }
  }
`

function PreActionLanding() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [toast, setToast] = useState({ message: '', type: '', visible: false })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const showToast = (message, type) => {
    setToast({ message, type, visible: true })
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 5000)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!name.trim()) {
      showToast('Please enter your full name.', 'error')
      return
    }
    if (!email.trim()) {
      showToast('Please enter your email address.', 'error')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('https://cac3vl23dkklu4rdxwki5yooxa0upfuq.lambda-url.eu-west-2.on.aws/capture-lead', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          'name': name,
          'work_email': email
        })
      })

      if (response.status === 422) {
        showToast('Please provide a valid work email. Personal email domains (Gmail, Hotmail) are not accepted.', 'error')
      } else if (response.status === 409) {
        showToast("You're already on our list. We'll reach out to you directly.", 'success')
      } else if (!response.ok) {
        showToast('Submission could not be processed. Please try again.', 'error')
      } else {
        showToast('Thanks. A member of the LexiTrove team will reply within 24 hours.', 'success')
        setName('')
        setEmail('')
      }
    } catch (err) {
      showToast('A network error occurred. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='home'>
      <Helmet>
        <title>Hearings Are Listed for 2028 | LexiTrove</title>
        <meta
          name='description'
          content='A two-minute briefing on why the pre-action phase is now where most employment disputes are resolved.'
        />
        {/* Campaign page: keep out of search results */}
        <meta name='robots' content='noindex, nofollow' />
        <style>{layoutCss}</style>
      </Helmet>

      <Navbar />

      <div className='pa-grid'>

        {/* LEFT COLUMN: THE 2-MINUTE BRIEFING */}
        <div className='pa-left'>
          <span style={{
            fontSize: '0.75rem',
            color: ACCENT,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: '600'
          }}>
            2-minute briefing
          </span>

          <h1 style={{
            fontSize: '2.25rem',
            lineHeight: '1.2',
            marginTop: '1rem',
            marginBottom: '1.5rem',
            fontWeight: 'normal',
            color: INK
          }}>
            Hearings are listed for 2028.{' '}
            <span style={{ color: ACCENT }}>Here's what that changes.</span>
          </h1>

          <p style={{ fontSize: '1rem', lineHeight: '1.8', color: '#333', marginBottom: '2.5rem' }}>
            Employment tribunal claims have reached a record 70,000, up 51% in a year, and multi-day
            hearings are being listed into 2028 and 2029. A matter opened today may not be heard for years.
          </p>

          <hr style={{ borderColor: 'black', marginBottom: '2.5rem', opacity: '0.2' }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {sections.map((s) => (
              <div key={s.number} style={{ display: 'grid', gridTemplateColumns: '44px 1fr', alignItems: 'start' }}>
                <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: '0.875rem', paddingTop: '2px' }}>
                  {s.number}
                </span>
                <div>
                  <strong style={{ fontSize: '1rem', display: 'block', marginBottom: '0.35rem', color: INK }}>
                    {s.title}
                  </strong>
                  <p style={{ fontSize: '0.875rem', color: '#444', lineHeight: '1.8', margin: 0 }}>
                    {s.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT COLUMN: PINNED CO-DESIGN FORM */}
        <div className='pa-right'>
          <div className='pa-sticky'>
            <div style={{
              border: '1px solid black',
              padding: '2.5rem 2rem',
              backgroundColor: PAPER
            }}>
              <h2 style={{ fontSize: '1.375rem', marginTop: 0, marginBottom: '1.5rem', fontWeight: 'normal', color: INK }}>
                Want to talk it through?
              </h2>
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={labelStyle}>Full Name</label>
                  <input
                    type='text'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder='e.g. Sarah Jenkins'
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: '1.75rem' }}>
                  <label style={labelStyle}>Email Address</label>
                  <input
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder='s.jenkins@lawfirm.co.uk'
                    style={inputStyle}
                  />
                </div>

                <button
                  type='submit'
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: ACCENT,
                    color: 'white',
                    border: 'none',
                    borderRadius: '2px',
                    fontFamily: "'Jost', sans-serif",
                    fontSize: '0.875rem',
                    letterSpacing: '0.05em',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                >
                  {isSubmitting ? 'Sending...' : "Let's talk"}
                </button>

                <p style={{ fontSize: '0.75rem', color: '#555', lineHeight: '1.6', marginTop: '1rem', marginBottom: 0, textAlign: 'center' }}>
                  Sharing your email doesn't oblige you to anything. No mailing list, no follow-up barrage.
                </p>
              </form>
            </div>

            {/* CREDIBILITY ANCHOR (deliberately below the form) */}
            <p style={{ fontSize: '0.75rem', color: '#555', marginTop: '1.25rem', lineHeight: '1.7' }}>
              Written by Rohit Kumar, Co-Founder, LexiTrove Labs.
            </p>
          </div>
        </div>

      </div>

      {/* TOAST NOTIFICATION MODAL */}
      {toast.visible && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 999
          }} />

          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: PAPER,
            border: `1px solid ${toast.type === 'success' ? ACCENT : INK}`,
            borderTop: `3px solid ${toast.type === 'success' ? ACCENT : INK}`,
            padding: '2rem',
            fontFamily: "'Jost', sans-serif",
            fontSize: '0.75rem',
            color: toast.type === 'success' ? ACCENT : INK,
            maxWidth: '380px',
            width: '90%',
            zIndex: 1000,
            lineHeight: '1.8',
            textAlign: 'center'
          }}>
            <p style={{ marginBottom: '1.5rem' }}>{toast.message}</p>
            <button
              onClick={() => setToast({ message: '', type: '', visible: false })}
              style={{
                fontFamily: "'Jost', sans-serif",
                fontSize: '0.75rem',
                backgroundColor: toast.type === 'success' ? ACCENT : INK,
                color: '#fff',
                border: 'none',
                padding: '6px 20px',
                cursor: 'pointer',
                letterSpacing: '0.06em',
                textTransform: 'uppercase'
              }}
            >
              Close
            </button>
          </div>
        </>
      )}
      <Footer />
    </div>
  )
}

export default PreActionLanding